(() => {
  if (customElements.get('the-list-browser')) return;

  class TheListBrowser extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      this.dataset.initialized = 'true';

      this.abortController = new AbortController();
      this.signal = this.abortController.signal;

      this.sectionId = this.dataset.sectionId || '';
      this.templateSuffix = this.dataset.templateSuffix || '';

      this.workspace = this.querySelector('[data-list-workspace]');
      this.pane = this.querySelector('[data-list-pane]');
      this.rowsContainer = this.querySelector(
        '[data-list-records]'
      );
      this.pagination = this.querySelector(
        '[data-list-pagination]'
      );
      this.viewer = this.querySelector('[data-list-viewer]');
      this.viewerInner = this.querySelector(
        '.the-list__viewer-inner'
      );
      this.viewerPrompt = this.querySelector(
        '[data-list-viewer-prompt]'
      );
      this.viewerContent = this.querySelector(
        '[data-list-viewer-content]'
      );
      this.viewerCopy = this.querySelector(
        '.the-list__viewer-copy'
      );
      this.viewerActions = this.querySelector(
        '.the-list__actions'
      );
      this.imageShell = this.querySelector(
        '[data-list-image-shell]'
      );
      this.collapseButton = this.querySelector(
        '[data-list-collapse]'
      );
      this.countElement = this.querySelector(
        '[data-list-count]'
      );
      this.loadStatus = this.querySelector(
        '[data-list-load-status]'
      );

      if (
        !this.workspace ||
        !this.pane ||
        !this.rowsContainer
      ) {
        return;
      }

      if (this.viewerActions) {
        this.actionsHomeMarker = document.createComment(
          'the-list-actions-home'
        );

        this.viewerActions.before(this.actionsHomeMarker);
      }

      this.querySelectorAll(
        'noscript, .pagination-wrapper'
      ).forEach((element) => {
        element.remove();
      });

      if (this.pagination) {
        this.pagination.hidden = true;
      }

      this.groups = new Map();
      this.representatives = new Map();
      this.productIds = new Set();

      this.selectedNumber = null;
      this.selectedProduct = null;
      this.sheetOpen = false;
      this.selectionVersion = 0;
      this.loadingComplete = false;
      this.shortHeightMode = false;

      this.lockedMobileViewportWidth = null;
      this.mobileViewerLockFrame = null;
      this.pendingMobileHeightReset = false;

      this.mobileMedia = window.matchMedia(
        '(max-width: 989px)'
      );

      this.reducedMotionMedia = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      );

      const initialRecords = Array.from(
        this.rowsContainer.querySelectorAll(
          '[data-list-record]'
        )
      );

      this.addRecords(initialRecords);
      this.renderRows({ preserveViewport: false });
      this.updateCount();
      this.bindEvents();
      this.updateResponsiveState();
      this.updatePaneScrollState();
      this.updateLayout();

      if (document.fonts?.ready) {
        document.fonts.ready.then(() => {
          if (this.isConnected) {
            this.scheduleLayout();
          }
        });
      }

      this.loadAllPages();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.headerObserver?.disconnect();
      this.viewerObserver?.disconnect();

      if (this.centerFrameOne) {
        cancelAnimationFrame(this.centerFrameOne);
      }

      if (this.centerFrameTwo) {
        cancelAnimationFrame(this.centerFrameTwo);
      }

      if (this.layoutFrame) {
        cancelAnimationFrame(this.layoutFrame);
      }

      if (this.viewerFitFrame) {
        cancelAnimationFrame(this.viewerFitFrame);
      }

      if (this.mobileViewerLockFrame) {
        cancelAnimationFrame(this.mobileViewerLockFrame);
      }
    }

    bindEvents() {
      this.rowsContainer.addEventListener(
        'click',
        (event) => {
          const row = event.target.closest('[data-list-row]');

          if (
            !row ||
            !this.rowsContainer.contains(row)
          ) {
            return;
          }

          if (
            event.button !== 0 ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey
          ) {
            return;
          }

          event.preventDefault();

          const number = Number.parseInt(
            row.dataset.listNumber,
            10
          );

          if (!Number.isFinite(number)) return;

          if (
            this.mobileMedia.matches &&
            this.sheetOpen &&
            this.selectedNumber === number
          ) {
            this.collapseMobileViewer();
            return;
          }

          this.selectNumber(number);
        },
        { signal: this.signal }
      );

      this.rowsContainer.addEventListener(
        'keydown',
        (event) => {
          if (event.key !== ' ') return;

          const row = event.target.closest('[data-list-row]');

          if (
            !row ||
            !this.rowsContainer.contains(row)
          ) {
            return;
          }

          event.preventDefault();

          const number = Number.parseInt(
            row.dataset.listNumber,
            10
          );

          if (!Number.isFinite(number)) return;

          if (
            this.mobileMedia.matches &&
            this.sheetOpen &&
            this.selectedNumber === number
          ) {
            this.collapseMobileViewer();
            return;
          }

          this.selectNumber(number);
        },
        { signal: this.signal }
      );

      this.collapseButton?.addEventListener(
        'click',
        () => {
          this.collapseMobileViewer();
        },
        { signal: this.signal }
      );

      this.pane.addEventListener(
        'scroll',
        () => {
          this.updatePaneScrollState();
        },
        {
          signal: this.signal,
          passive: true
        }
      );

      this.pane.addEventListener(
        'wheel',
        (event) => {
          this.handlePaneWheel(event);
        },
        {
          signal: this.signal,
          passive: false
        }
      );

      this.mobileMedia.addEventListener(
        'change',
        () => {
          this.clearMobileViewerLock();
          this.updateResponsiveState();
          this.scheduleLayout();
        },
        { signal: this.signal }
      );

      window.addEventListener(
        'resize',
        () => {
          this.handleViewportResize();
        },
        {
          signal: this.signal,
          passive: true
        }
      );

      if (window.visualViewport) {
        window.visualViewport.addEventListener(
          'resize',
          () => {
            this.handleViewportResize();
          },
          {
            signal: this.signal,
            passive: true
          }
        );
      }

      const themeHeader = document.querySelector(
        '#shopify-section-header'
      );

      if (
        themeHeader &&
        'ResizeObserver' in window
      ) {
        this.headerObserver = new ResizeObserver(() => {
          this.scheduleLayout();
        });

        this.headerObserver.observe(themeHeader);
      }

      if (
        this.viewer &&
        this.workspace &&
        'ResizeObserver' in window
      ) {
        this.viewerObserver = new ResizeObserver(() => {
          if (
            this.mobileMedia.matches &&
            this.sheetOpen
          ) {
            this.updateMobileSheetOccupiedHeight();
            return;
          }

          this.scheduleViewerFit();
        });

        this.viewerObserver.observe(this.viewer);
      }
    }

    getStableViewportWidth() {
      return Math.round(
        document.documentElement.clientWidth ||
        window.innerWidth ||
        0
      );
    }

    handleViewportResize() {
      if (
        this.mobileMedia.matches &&
        this.sheetOpen &&
        this.lockedMobileViewportWidth !== null
      ) {
        const currentWidth =
          this.getStableViewportWidth();

        const widthChanged =
          Math.abs(
            currentWidth -
            this.lockedMobileViewportWidth
          ) >= 2;

        if (!widthChanged) {
          /*
           * Safari's address bar has changed the visual viewport
           * height, but the page width has not changed.
           *
           * Keep the viewer, image and text dimensions locked.
           * The fixed sheet can move with Safari as one unit.
           */
          this.updateMobileSheetOccupiedHeight();
          return;
        }

        /*
         * A real width change normally means an orientation change
         * or a genuine resize. Recalculate the mobile viewer.
         */
        this.clearMobileViewerLock();
        this.scheduleMobileViewerLock({
          resetHeight: true
        });
      }

      this.scheduleLayout();
    }

    updatePaneScrollState() {
      const isScrolled = this.pane.scrollTop > 2;

      this.classList.toggle(
        'is-list-scrolled',
        isScrolled
      );
    }

    handlePaneWheel(event) {
      if (event.deltaY === 0) return;

      const atTop = this.pane.scrollTop <= 0;

      const maximumScrollTop = Math.max(
        0,
        this.pane.scrollHeight -
        this.pane.clientHeight
      );

      const atBottom =
        this.pane.scrollTop >= maximumScrollTop - 1;

      const leavingTop =
        event.deltaY < 0 && atTop;

      const leavingBottom =
        event.deltaY > 0 && atBottom;

      if (!leavingTop && !leavingBottom) return;

      event.preventDefault();

      window.scrollBy({
        top: event.deltaY,
        left: 0,
        behavior: 'auto'
      });
    }

    scheduleLayout() {
      if (this.layoutFrame) {
        cancelAnimationFrame(this.layoutFrame);
      }

      this.layoutFrame = requestAnimationFrame(() => {
        this.layoutFrame = null;
        this.updateLayout();
      });
    }

    updateLayout() {
      this.updateHeaderHeight();
      this.updateWorkspaceHeight();
      this.scheduleViewerFit();

      if (
        this.mobileMedia.matches &&
        this.sheetOpen
      ) {
        this.updateMobileSheetOccupiedHeight();
      }
    }

    updateHeaderHeight() {
      const themeHeader = document.querySelector(
        '#shopify-section-header'
      );

      const headerHeight = themeHeader
        ? themeHeader.getBoundingClientRect().height
        : 0;

      this.style.setProperty(
        '--list-theme-header-height',
        `${Math.max(0, headerHeight)}px`
      );
    }

    getCssPixels(propertyName, fallback = 0) {
      const value = Number.parseFloat(
        getComputedStyle(this).getPropertyValue(
          propertyName
        )
      );

      return Number.isFinite(value)
        ? value
        : fallback;
    }

    updateWorkspaceHeight() {
      if (!this.workspace) return;

      if (this.mobileMedia.matches) {
        this.style.removeProperty(
          '--list-workspace-height'
        );

        this.setShortHeightMode(false);
        return;
      }

      const viewportHeight =
        window.visualViewport?.height ||
        window.innerHeight;

      const workspaceTop =
        this.workspace.getBoundingClientRect().top;

      const sectionBottomPadding = this.getCssPixels(
        '--list-section-padding-bottom',
        32
      );

      const availableHeight =
        viewportHeight -
        Math.max(0, workspaceTop) -
        sectionBottomPadding -
        8;

      const workspaceHeight = Math.max(
        380,
        Math.floor(availableHeight)
      );

      this.style.setProperty(
        '--list-workspace-height',
        `${workspaceHeight}px`
      );

      this.updateShortHeightMode(workspaceHeight);
    }

    updateShortHeightMode(workspaceHeight) {
      let shouldUseShortHeightMode;

      if (this.shortHeightMode) {
        shouldUseShortHeightMode =
          workspaceHeight < 600;
      } else {
        shouldUseShortHeightMode =
          workspaceHeight <= 560;
      }

      this.setShortHeightMode(
        shouldUseShortHeightMode
      );
    }

    setShortHeightMode(enabled) {
      if (this.mobileMedia.matches) {
        enabled = false;
      }

      if (this.shortHeightMode === enabled) return;

      this.shortHeightMode = enabled;

      this.classList.toggle(
        'is-short-height',
        enabled
      );

      if (!this.viewer) return;

      if (enabled) {
        this.viewer.style.setProperty(
          'padding-top',
          '0px'
        );

        this.viewer.style.setProperty(
          'padding-bottom',
          '0px'
        );
      } else {
        this.viewer.style.removeProperty(
          'padding-top'
        );

        this.viewer.style.removeProperty(
          'padding-bottom'
        );
      }
    }

    scheduleViewerFit() {
      if (
        this.mobileMedia.matches
      ) {
        if (this.sheetOpen) {
          this.updateMobileSheetOccupiedHeight();
        }

        return;
      }

      if (this.viewerFitFrame) {
        cancelAnimationFrame(
          this.viewerFitFrame
        );
      }

      this.viewerFitFrame =
        requestAnimationFrame(() => {
          this.viewerFitFrame = null;
          this.fitViewerToViewport();
        });
    }

    clearViewerFit() {
      this.style.removeProperty(
        '--list-fitted-card-width'
      );

      this.style.removeProperty(
        '--list-viewer-scale'
      );

      this.style.removeProperty(
        '--list-fitted-top-offset'
      );
    }

    fitViewerToViewport() {
      if (
        this.mobileMedia.matches ||
        !this.viewer ||
        !this.viewerInner ||
        !this.viewerContent ||
        this.viewerContent.hidden
      ) {
        this.clearViewerFit();
        return;
      }

      const configuredCardWidth = Math.max(
        1,
        this.getCssPixels(
          '--list-desktop-card-width',
          440
        )
      );

      const normalTopOffset = Math.max(
        0,
        this.getCssPixels(
          '--list-viewer-top-offset',
          0
        )
      );

      const configuredTopOffset =
        this.shortHeightMode
          ? 0
          : normalTopOffset;

      this.style.setProperty(
        '--list-viewer-scale',
        '1'
      );

      const viewerStyles =
        getComputedStyle(this.viewer);

      const horizontalPadding =
        Number.parseFloat(
          viewerStyles.paddingLeft || '0'
        ) +
        Number.parseFloat(
          viewerStyles.paddingRight || '0'
        );

      const viewerWidth = Math.max(
        1,
        this.viewer.clientWidth -
        horizontalPadding
      );

      const viewerMaximumWidth = Math.max(
        1,
        this.getCssPixels(
          '--list-viewer-max-width',
          680
        )
      );

      const maximumAllowedWidth =
        this.shortHeightMode
          ? Math.min(
              viewerWidth,
              viewerMaximumWidth
            )
          : Math.min(
              configuredCardWidth,
              viewerWidth,
              viewerMaximumWidth
            );

      const maximumCardWidth = Math.max(
        1,
        maximumAllowedWidth
      );

      const minimumCardWidth = Math.min(
        maximumCardWidth,
        Math.max(
          160,
          configuredCardWidth * 0.42
        )
      );

      const measure = (cardWidth) => {
        const scale = Math.min(
          1,
          cardWidth / configuredCardWidth
        );

        this.style.setProperty(
          '--list-fitted-card-width',
          `${cardWidth.toFixed(2)}px`
        );

        this.style.setProperty(
          '--list-viewer-scale',
          scale.toFixed(4)
        );

        this.style.setProperty(
          '--list-fitted-top-offset',
          `${(
            configuredTopOffset * scale
          ).toFixed(2)}px`
        );

        const currentViewerStyles =
          getComputedStyle(this.viewer);

        const verticalPadding =
          Number.parseFloat(
            currentViewerStyles.paddingTop || '0'
          ) +
          Number.parseFloat(
            currentViewerStyles.paddingBottom || '0'
          );

        const availableHeight = Math.max(
          1,
          this.viewer.clientHeight -
          verticalPadding
        );

        const requiredHeight =
          this.viewerInner.scrollHeight +
          configuredTopOffset * scale;

        return {
          fits:
            requiredHeight <=
            availableHeight + 1,
          availableHeight,
          requiredHeight
        };
      };

      const maximumMeasurement =
        measure(maximumCardWidth);

      if (maximumMeasurement.fits) return;

      let lowerWidth = minimumCardWidth;
      let upperWidth = maximumCardWidth;

      let lowerMeasurement =
        measure(lowerWidth);

      if (!lowerMeasurement.fits) {
        const extraHeight = Math.ceil(
          lowerMeasurement.requiredHeight -
          lowerMeasurement.availableHeight
        );

        const expandedWorkspaceHeight =
          this.workspace.clientHeight +
          Math.max(0, extraHeight) +
          4;

        this.style.setProperty(
          '--list-workspace-height',
          `${expandedWorkspaceHeight}px`
        );

        lowerMeasurement = measure(lowerWidth);

        if (!lowerMeasurement.fits) return;
      }

      for (
        let iteration = 0;
        iteration < 10;
        iteration += 1
      ) {
        const middleWidth =
          (lowerWidth + upperWidth) / 2;

        const middleMeasurement =
          measure(middleWidth);

        if (middleMeasurement.fits) {
          lowerWidth = middleWidth;
        } else {
          upperWidth = middleWidth;
        }
      }

      measure(lowerWidth);
    }

    placeViewerActions() {
      if (
        !this.viewerActions ||
        !this.viewerInner
      ) {
        return;
      }

      if (this.mobileMedia.matches) {
        if (
          this.viewerActions.parentElement !==
          this.viewerInner
        ) {
          this.viewerInner.append(
            this.viewerActions
          );
        }

        return;
      }

      if (
        this.actionsHomeMarker?.isConnected
      ) {
        this.actionsHomeMarker.after(
          this.viewerActions
        );
      }
    }

    clearMobileViewerLock() {
      if (this.mobileViewerLockFrame) {
        cancelAnimationFrame(
          this.mobileViewerLockFrame
        );

        this.mobileViewerLockFrame = null;
      }

      this.pendingMobileHeightReset = false;
      this.lockedMobileViewportWidth = null;

      this.style.removeProperty(
        '--list-mobile-locked-sheet-height'
      );

      this.style.removeProperty(
        '--list-mobile-locked-card-width'
      );

      this.style.removeProperty(
        '--list-mobile-sheet-occupied-height'
      );
    }

    scheduleMobileViewerLock({
      resetHeight = false
    } = {}) {
      if (
        !this.mobileMedia.matches ||
        !this.sheetOpen
      ) {
        return;
      }

      this.pendingMobileHeightReset =
        this.pendingMobileHeightReset ||
        resetHeight;

      if (this.mobileViewerLockFrame) {
        cancelAnimationFrame(
          this.mobileViewerLockFrame
        );
      }

      this.mobileViewerLockFrame =
        requestAnimationFrame(() => {
          this.mobileViewerLockFrame = null;

          const shouldResetHeight =
            this.pendingMobileHeightReset;

          this.pendingMobileHeightReset = false;

          this.lockMobileViewerDimensions({
            resetHeight: shouldResetHeight
          });
        });
    }

    lockMobileViewerDimensions({
      resetHeight = false
    } = {}) {
      if (
        !this.mobileMedia.matches ||
        !this.sheetOpen ||
        !this.viewer
      ) {
        return;
      }

      this.placeViewerActions();

      if (resetHeight) {
        this.style.removeProperty(
          '--list-mobile-locked-sheet-height'
        );

        this.style.removeProperty(
          '--list-mobile-locked-card-width'
        );
      }

      const currentSheetHeight = Math.round(
        this.viewer.getBoundingClientRect().height
      );

      if (currentSheetHeight > 0) {
        this.style.setProperty(
          '--list-mobile-locked-sheet-height',
          `${currentSheetHeight}px`
        );
      }

      /*
       * Setting the sheet height first ensures that the image is
       * measured inside its final, fixed-height viewer.
       */
      this.viewer.getBoundingClientRect();

      const imageShell =
        this.imageShell ||
        this.viewer.querySelector(
          '[data-list-image-shell]'
        );

      if (imageShell) {
        const measuredCardWidth = Math.round(
          imageShell.getBoundingClientRect().width
        );

        if (measuredCardWidth > 0) {
          this.style.setProperty(
            '--list-mobile-locked-card-width',
            `${measuredCardWidth}px`
          );
        }
      }

      this.lockedMobileViewportWidth =
        this.getStableViewportWidth();

      this.updateMobileSheetOccupiedHeight();
    }

    updateMobileSheetOccupiedHeight() {
      if (
        !this.mobileMedia.matches ||
        !this.sheetOpen ||
        !this.viewer ||
        !this.workspace
      ) {
        this.style.removeProperty(
          '--list-mobile-sheet-occupied-height'
        );

        return;
      }

      const workspaceRect =
        this.workspace.getBoundingClientRect();

      const viewerRect =
        this.viewer.getBoundingClientRect();

      const occupiedHeight = Math.max(
        0,
        Math.min(
          workspaceRect.height,
          workspaceRect.bottom -
          viewerRect.top
        )
      );

      this.style.setProperty(
        '--list-mobile-sheet-occupied-height',
        `${Math.ceil(occupiedHeight)}px`
      );
    }

    parseRecord(element) {
      const rawNumber = String(
        element.dataset.listNumber || ''
      );

      const number = Number.parseInt(
        rawNumber.replace(/[^\d]/g, ''),
        10
      );

      if (
        !Number.isFinite(number) ||
        number <= 0
      ) {
        return null;
      }

      const productId = String(
        element.dataset.productId || ''
      );

      if (
        !productId ||
        this.productIds.has(productId)
      ) {
        return null;
      }

      return {
        productId,
        variantId: String(
          element.dataset.productVariantId || ''
        ),
        variantAvailable:
          element.dataset
            .productVariantAvailable === 'true',
        number,
        numberDisplay:
          element.dataset.listNumberDisplay ||
          String(number).padStart(3, '0'),
        title:
          element.dataset.productTitle || '',
        vendor:
          element.dataset.productVendor || '',
        url:
          element.dataset.productUrl ||
          element.getAttribute('href') ||
          '',
        available:
          element.dataset.productAvailable ===
          'true',
        price:
          element.dataset.productPrice || '',
        priceCents: Number.parseInt(
          element.dataset.productPriceCents ||
          '0',
          10
        ),
        priceVaries:
          element.dataset.productPriceVaries ===
          'true',
        compareAt:
          element.dataset.productCompareAt || '',
        compareAtCents: Number.parseInt(
          element.dataset
            .productCompareAtCents || '0',
          10
        ),
        editorial:
          element.dataset.productEditorial || '',
        image600:
          element.getAttribute(
            'data-product-image-600'
          ) || '',
        image900:
          element.getAttribute(
            'data-product-image-900'
          ) || '',
        image1200:
          element.getAttribute(
            'data-product-image-1200'
          ) || '',
        imageWidth: Number.parseInt(
          element.dataset.productImageWidth ||
          '0',
          10
        ),
        imageHeight: Number.parseInt(
          element.dataset.productImageHeight ||
          '0',
          10
        ),
        imageAlt:
          element.dataset.productImageAlt ||
          element.dataset.productTitle ||
          ''
      };
    }

    addRecords(elements) {
      let addedCount = 0;

      elements.forEach((element) => {
        const product =
          this.parseRecord(element);

        if (!product) return;

        this.productIds.add(product.productId);

        if (!this.groups.has(product.number)) {
          this.groups.set(
            product.number,
            []
          );
        }

        this.groups
          .get(product.number)
          .push(product);

        addedCount += 1;
      });

      return addedCount;
    }

    compareProductIds(
      firstProduct,
      secondProduct
    ) {
      return firstProduct.productId.localeCompare(
        secondProduct.productId,
        undefined,
        { numeric: true }
      );
    }

    chooseRepresentative(products) {
      const availableProducts = products
        .filter((product) => product.available)
        .sort((firstProduct, secondProduct) =>
          this.compareProductIds(
            firstProduct,
            secondProduct
          )
        );

      if (availableProducts.length > 0) {
        return availableProducts[0];
      }

      return [...products].sort(
        (firstProduct, secondProduct) =>
          this.compareProductIds(
            firstProduct,
            secondProduct
          )
      )[0];
    }

    buildRepresentatives() {
      this.representatives.clear();

      this.groups.forEach(
        (products, number) => {
          const representative =
            this.chooseRepresentative(products);

          if (representative) {
            this.representatives.set(
              number,
              representative
            );
          }
        }
      );
    }

    getSortedNumbers() {
      return Array.from(
        this.representatives.keys()
      ).sort(
        (firstNumber, secondNumber) =>
          firstNumber - secondNumber
      );
    }

    stripBracketedText(title) {
      let cleanedTitle = String(title || '');
      let previousTitle = '';

      while (
        cleanedTitle !== previousTitle &&
        /[\(\[\{]/.test(cleanedTitle)
      ) {
        previousTitle = cleanedTitle;

        cleanedTitle = cleanedTitle.replace(
          /\([^()]*\)|\[[^\[\]]*\]|\{[^{}]*\}/g,
          ' '
        );
      }

      return cleanedTitle
        .replace(/\s+/g, ' ')
        .replace(/\s+([,.;:!?])/g, '$1')
        .trim();
    }

    createRow(product) {
      const row = document.createElement('a');
      const number = product.number;

      const listTitle =
        this.stripBracketedText(product.title) ||
        product.title;

      row.className = 'the-list__row';
      row.href = product.url;
      row.dataset.listRow = '';
      row.dataset.listNumber = String(number);
      row.dataset.productId =
        product.productId;

      row.setAttribute(
        'aria-label',
        `Select List item ${number}, ${listTitle}${
          product.available
            ? ''
            : ', sold out'
        }`
      );

      if (this.selectedNumber === number) {
        row.classList.add('is-selected');
        row.setAttribute(
          'aria-current',
          'true'
        );
      }

      const numberElement =
        document.createElement('span');

      numberElement.className =
        'the-list__row-number';

      numberElement.setAttribute(
        'aria-hidden',
        'true'
      );

      numberElement.textContent =
        `№${product.numberDisplay}`;

      const titleElement =
        document.createElement('span');

      titleElement.className =
        'the-list__row-title';

      titleElement.setAttribute(
        'aria-hidden',
        'true'
      );

      titleElement.textContent = listTitle;

      row.append(
        numberElement,
        titleElement
      );

      return row;
    }

    captureViewportAnchor() {
      const paneRect =
        this.pane.getBoundingClientRect();

      const selectedRow =
        this.getSelectedRow();

      if (selectedRow) {
        const selectedRect =
          selectedRow.getBoundingClientRect();

        const selectedIsVisible =
          selectedRect.bottom >
            paneRect.top &&
          selectedRect.top <
            paneRect.bottom;

        if (selectedIsVisible) {
          return {
            number: this.selectedNumber,
            offset:
              selectedRect.top -
              paneRect.top
          };
        }
      }

      const rows = Array.from(
        this.rowsContainer.querySelectorAll(
          '[data-list-row]'
        )
      );

      const firstVisibleRow = rows.find(
        (row) => {
          return (
            row.getBoundingClientRect().bottom >
            paneRect.top
          );
        }
      );

      if (!firstVisibleRow) return null;

      return {
        number: Number.parseInt(
          firstVisibleRow.dataset.listNumber,
          10
        ),
        offset:
          firstVisibleRow
            .getBoundingClientRect().top -
          paneRect.top
      };
    }

    restoreViewportAnchor(anchor) {
      if (!anchor) return;

      const matchingRow = Array.from(
        this.rowsContainer.querySelectorAll(
          '[data-list-row]'
        )
      ).find((row) => {
        return (
          Number.parseInt(
            row.dataset.listNumber,
            10
          ) === anchor.number
        );
      });

      if (!matchingRow) return;

      const paneRect =
        this.pane.getBoundingClientRect();

      const newOffset =
        matchingRow
          .getBoundingClientRect().top -
        paneRect.top;

      const adjustment =
        newOffset - anchor.offset;

      if (adjustment !== 0) {
        this.pane.scrollTop += adjustment;
      }
    }

    renderRows({
      preserveViewport = true
    } = {}) {
      const anchor = preserveViewport
        ? this.captureViewportAnchor()
        : null;

      const previousSelectedProductId =
        this.selectedProduct?.productId ||
        null;

      this.buildRepresentatives();

      const fragment =
        document.createDocumentFragment();

      this.getSortedNumbers().forEach(
        (number) => {
          const product =
            this.representatives.get(number);

          if (product) {
            fragment.append(
              this.createRow(product)
            );
          }
        }
      );

      this.rowsContainer.replaceChildren(
        fragment
      );

      this.restoreViewportAnchor(anchor);
      this.updatePaneScrollState();

      if (this.selectedNumber !== null) {
        const currentRepresentative =
          this.representatives.get(
            this.selectedNumber
          );

        if (
          currentRepresentative &&
          currentRepresentative.productId !==
            previousSelectedProductId
        ) {
          this.selectedProduct =
            currentRepresentative;

          this.updateViewer(
            currentRepresentative
          );

          this.markSelectedRow(
            this.selectedNumber
          );
        }
      }

      this.updateCount();
    }

    getSelectedRow() {
      if (this.selectedNumber === null) {
        return null;
      }

      return Array.from(
        this.rowsContainer.querySelectorAll(
          '[data-list-row]'
        )
      ).find((row) => {
        return (
          Number.parseInt(
            row.dataset.listNumber,
            10
          ) === this.selectedNumber
        );
      });
    }

    markSelectedRow(number) {
      this.rowsContainer
        .querySelectorAll(
          '[data-list-row].is-selected'
        )
        .forEach((row) => {
          row.classList.remove(
            'is-selected'
          );

          row.removeAttribute(
            'aria-current'
          );
        });

      const selectedRow = Array.from(
        this.rowsContainer.querySelectorAll(
          '[data-list-row]'
        )
      ).find((row) => {
        return (
          Number.parseInt(
            row.dataset.listNumber,
            10
          ) === number
        );
      });

      if (selectedRow) {
        selectedRow.classList.add(
          'is-selected'
        );

        selectedRow.setAttribute(
          'aria-current',
          'true'
        );
      }

      return selectedRow;
    }

    selectNumber(number) {
      const representative =
        this.representatives.get(number);

      if (!representative) return;

      this.selectionVersion += 1;

      const currentSelectionVersion =
        this.selectionVersion;

      this.selectedNumber = number;
      this.selectedProduct =
        representative;

      const selectedRow =
        this.markSelectedRow(number);

      this.updateViewer(representative);

      if (!selectedRow) return;

      if (this.mobileMedia.matches) {
        this.openMobileViewer();

        this.centerFrameOne =
          requestAnimationFrame(() => {
            this.centerFrameTwo =
              requestAnimationFrame(() => {
                if (
                  currentSelectionVersion !==
                  this.selectionVersion
                ) {
                  return;
                }

                this.centerRow(selectedRow);
              });
          });
      } else {
        this.centerFrameOne =
          requestAnimationFrame(() => {
            if (
              currentSelectionVersion !==
              this.selectionVersion
            ) {
              return;
            }

            this.centerRow(selectedRow);
          });
      }
    }

    centerRow(row) {
      const targetScrollTop =
        row.offsetTop -
        (
          this.pane.clientHeight -
          row.offsetHeight
        ) /
          2;

      const maximumScrollTop =
        this.pane.scrollHeight -
        this.pane.clientHeight;

      const finalScrollTop = Math.max(
        0,
        Math.min(
          targetScrollTop,
          maximumScrollTop
        )
      );

      this.pane.scrollTo({
        top: finalScrollTop,
        behavior:
          this.reducedMotionMedia.matches
            ? 'auto'
            : 'smooth'
      });
    }

    openMobileViewer() {
      const wasClosed = !this.sheetOpen;

      this.sheetOpen = true;
      this.classList.add('is-sheet-open');

      this.viewer?.setAttribute(
        'aria-hidden',
        'false'
      );

      this.placeViewerActions();

      if (wasClosed) {
        this.clearMobileViewerLock();

        this.scheduleMobileViewerLock({
          resetHeight: true
        });
      } else {
        this.updateMobileSheetOccupiedHeight();
      }
    }

    collapseMobileViewer() {
      if (!this.mobileMedia.matches) return;

      this.sheetOpen = false;
      this.classList.remove('is-sheet-open');

      this.viewer?.setAttribute(
        'aria-hidden',
        'true'
      );

      this.style.removeProperty(
        '--list-mobile-sheet-occupied-height'
      );
    }

    updateResponsiveState() {
      if (!this.viewer) return;

      this.placeViewerActions();

      if (this.mobileMedia.matches) {
        this.viewer.setAttribute(
          'aria-hidden',
          this.sheetOpen
            ? 'false'
            : 'true'
        );

        this.setShortHeightMode(false);
        this.clearViewerFit();

        if (this.sheetOpen) {
          this.scheduleMobileViewerLock({
            resetHeight: true
          });
        }
      } else {
        this.viewer.removeAttribute(
          'aria-hidden'
        );

        this.clearMobileViewerLock();
        this.placeViewerActions();
        this.scheduleViewerFit();
      }
    }

    updateViewer(product) {
      if (
        !this.viewer ||
        !this.viewerContent ||
        !this.viewerPrompt
      ) {
        return;
      }

      const productLinks =
        this.viewer.querySelectorAll(
          '[data-list-product-link]'
        );

      productLinks.forEach((link) => {
        link.href = product.url;
      });

      const image =
        this.viewer.querySelector(
          '[data-list-image]'
        );

      const imageShell =
        this.viewer.querySelector(
          '[data-list-image-shell]'
        );

      const vendor =
        this.viewer.querySelector(
          '[data-list-vendor]'
        );

      const title =
        this.viewer.querySelector(
          '[data-list-title]'
        );

      const currentPrice =
        this.viewer.querySelector(
          '[data-list-current-price]'
        );

      const comparePrice =
        this.viewer.querySelector(
          '[data-list-compare-price]'
        );

      const editorial =
        this.viewer.querySelector(
          '[data-list-editorial]'
        );

      const variantInput =
        this.viewer.querySelector(
          '[data-list-variant-input]'
        );

      const addButton =
        this.viewer.querySelector(
          '[data-list-add-button]'
        );

      const addButtonLabel =
        this.viewer.querySelector(
          '[data-list-add-button-label]'
        );

      if (vendor) {
        vendor.textContent =
          product.vendor;
      }

      if (title) {
        /*
         * The full product name is retained in the viewer.
         * Only the name shown in the numbered List has its
         * bracketed text removed.
         */
        title.textContent =
          product.title;
      }

      if (currentPrice) {
        currentPrice.textContent =
          `${
            product.priceVaries
              ? 'From '
              : ''
          }${product.price}`;
      }

      const isOnSale =
        product.compareAtCents >
          product.priceCents &&
        product.priceCents > 0;

      if (comparePrice) {
        comparePrice.hidden = !isOnSale;

        comparePrice.textContent =
          isOnSale
            ? product.compareAt
            : '';
      }

      if (image) {
        if (product.image900) {
          image.hidden = false;
          image.src = product.image900;

          image.srcset = [
            product.image600
              ? `${product.image600} 600w`
              : '',
            product.image900
              ? `${product.image900} 900w`
              : '',
            product.image1200
              ? `${product.image1200} 1200w`
              : ''
          ]
            .filter(Boolean)
            .join(', ');

          image.sizes =
            this.mobileMedia.matches
              ? '90vw'
              : `${Math.min(
                  product.imageWidth ||
                    900,
                  900
                )}px`;

          image.alt = product.imageAlt;

          if (product.imageWidth > 0) {
            image.width =
              product.imageWidth;
          }

          if (product.imageHeight > 0) {
            image.height =
              product.imageHeight;
          }

          if (!image.complete) {
            image.addEventListener(
              'load',
              () => {
                if (
                  this.mobileMedia.matches &&
                  this.sheetOpen
                ) {
                  this.updateMobileSheetOccupiedHeight();
                } else {
                  this.scheduleViewerFit();
                }
              },
              { once: true }
            );
          }
        } else {
          image.hidden = true;
          image.removeAttribute('src');
          image.removeAttribute('srcset');
          image.alt = '';
        }
      }

      if (imageShell) {
        imageShell.classList.toggle(
          'is-sold-out',
          !product.available
        );
      }

      if (editorial) {
        const showEditorial =
          this.dataset.showEditorial ===
            'true' &&
          product.editorial.length > 0;

        editorial.hidden = !showEditorial;

        editorial.textContent =
          showEditorial
            ? product.editorial
            : '';
      }

      const canAddToCart =
        product.available &&
        product.variantAvailable &&
        product.variantId.length > 0;

      if (variantInput) {
        variantInput.value =
          product.variantId;

        variantInput.disabled =
          !canAddToCart;
      }

      if (addButton) {
        addButton.disabled =
          !canAddToCart;

        if (canAddToCart) {
          addButton.removeAttribute(
            'aria-disabled'
          );
        } else {
          addButton.setAttribute(
            'aria-disabled',
            'true'
          );
        }
      }

      if (addButtonLabel) {
        addButtonLabel.textContent =
          canAddToCart
            ? this.dataset.addLabel ||
              'ADD TO CART'
            : this.dataset.soldOutLabel ||
              'SOLD OUT';
      }

      this.viewerPrompt.hidden = true;
      this.viewerContent.hidden = false;

      if (
        this.mobileMedia.matches &&
        this.sheetOpen
      ) {
        /*
         * Preserve the already locked image and sheet dimensions
         * when another product is selected. Only the content changes.
         */
        this.updateMobileSheetOccupiedHeight();
      } else {
        this.scheduleViewerFit();
      }
    }

    formatCount() {
      const count = String(
        this.representatives.size
      );

      const before = String(
        this.dataset.countBefore || ''
      ).trim();

      const after = String(
        this.dataset.countAfter || ''
      ).trim();

      let result = before;

      if (result) {
        const finalCharacter =
          result.slice(-1);

        if (
          !/[\s([{£$€¥#№]/.test(
            finalCharacter
          )
        ) {
          result += ' ';
        }
      }

      result += count;

      if (after) {
        const firstCharacter =
          after.charAt(0);

        if (
          !/[\s)\]},.!?;:%]/.test(
            firstCharacter
          )
        ) {
          result += ' ';
        }

        result += after;
      }

      return result;
    }

    updateCount() {
      if (!this.countElement) return;

      this.countElement.textContent =
        this.formatCount();
    }

    finishLoading() {
      this.loadingComplete = true;
      this.updateCount();

      if (this.loadStatus) {
        this.loadStatus.hidden = true;
        this.loadStatus.textContent = '';
      }
    }

    showLoadingError() {
      if (!this.loadStatus) return;

      this.loadStatus.hidden = false;

      this.loadStatus.textContent =
        'The complete List could not be loaded. Reload the page to try again.';
    }

    getCurrentPageNumber() {
      const currentUrl = new URL(
        window.location.href
      );

      const page = Number.parseInt(
        currentUrl.searchParams.get('page') ||
          '1',
        10
      );

      return (
        Number.isFinite(page) &&
        page > 0
      )
        ? page
        : 1;
    }

    getFirstPageUrl() {
      const firstPageUrl = new URL(
        window.location.href
      );

      firstPageUrl.searchParams.delete(
        'section_id'
      );

      firstPageUrl.searchParams.delete(
        'page'
      );

      if (
        this.templateSuffix &&
        !firstPageUrl.searchParams.has(
          'view'
        )
      ) {
        firstPageUrl.searchParams.set(
          'view',
          this.templateSuffix
        );
      }

      return firstPageUrl.toString();
    }

    buildPageRequest(nextUrl) {
      const currentUrl = new URL(
        window.location.href
      );

      const paginationUrl = new URL(
        nextUrl,
        currentUrl
      );

      currentUrl.searchParams.delete(
        'section_id'
      );

      currentUrl.searchParams.delete(
        'page'
      );

      paginationUrl.searchParams.forEach(
        (value, key) => {
          if (key !== 'section_id') {
            currentUrl.searchParams.set(
              key,
              value
            );
          }
        }
      );

      if (
        this.templateSuffix &&
        !currentUrl.searchParams.has(
          'view'
        )
      ) {
        currentUrl.searchParams.set(
          'view',
          this.templateSuffix
        );
      }

      return currentUrl;
    }

    findIncomingBrowser(parsedDocument) {
      if (this.sectionId) {
        const matchingBrowser = Array.from(
          parsedDocument.querySelectorAll(
            'the-list-browser'
          )
        ).find((browser) => {
          return (
            browser.dataset.sectionId ===
            this.sectionId
          );
        });

        if (matchingBrowser) {
          return matchingBrowser;
        }
      }

      return parsedDocument.querySelector(
        'the-list-browser'
      );
    }

    async loadAllPages() {
      const currentPage =
        this.getCurrentPageNumber();

      let nextUrl =
        currentPage > 1
          ? this.getFirstPageUrl()
          : this.pagination?.dataset
              .nextUrl || '';

      if (!nextUrl) {
        this.finishLoading();
        return;
      }

      const requestedUrls = new Set();

      try {
        while (nextUrl) {
          const requestUrl =
            this.buildPageRequest(nextUrl);

          const requestKey =
            requestUrl.toString();

          if (
            requestedUrls.has(requestKey)
          ) {
            throw new Error(
              'The same pagination URL was returned more than once'
            );
          }

          requestedUrls.add(requestKey);

          const response = await fetch(
            requestUrl,
            {
              headers: {
                'X-Requested-With':
                  'XMLHttpRequest'
              },
              cache: 'no-store',
              credentials: 'same-origin',
              signal: this.signal
            }
          );

          if (!response.ok) {
            throw new Error(
              `Unable to load List page: ${response.status}`
            );
          }

          const responseText =
            await response.text();

          const parsedDocument =
            new DOMParser().parseFromString(
              responseText,
              'text/html'
            );

          const incomingBrowser =
            this.findIncomingBrowser(
              parsedDocument
            );

          if (!incomingBrowser) {
            throw new Error(
              'The List section was not found in the response'
            );
          }

          const incomingRowsContainer =
            incomingBrowser.querySelector(
              '[data-list-records]'
            );

          const incomingRecords =
            incomingRowsContainer
              ? Array.from(
                  incomingRowsContainer.querySelectorAll(
                    '[data-list-record]'
                  )
                )
              : [];

          const incomingPagination =
            incomingBrowser.querySelector(
              '[data-list-pagination]'
            );

          if (
            incomingRecords.length === 0
          ) {
            throw new Error(
              'No List products were found in the requested page'
            );
          }

          const addedCount =
            this.addRecords(
              incomingRecords
            );

          if (addedCount > 0) {
            this.renderRows({
              preserveViewport: true
            });
          }

          nextUrl =
            incomingPagination?.dataset
              .nextUrl || '';
        }

        this.finishLoading();
      } catch (error) {
        if (
          error.name === 'AbortError'
        ) {
          return;
        }

        console.error(
          'The List pagination failed:',
          error
        );

        this.showLoadingError();
      }
    }
  }

  customElements.define(
    'the-list-browser',
    TheListBrowser
  );
})();